/**
 * /app/{slug}/compliance — Compliance Command Center.
 *
 * Single consolidated dashboard showing next-due-dates for every Indian
 * SMB statutory compliance obligation. HR-admin only (mirrors backend
 * RBAC: company_admin + hr_manager on GET /api/v1/compliance/dashboard).
 *
 * Card inventory (11 items — all server-computed):
 *   1.  PF/ECR monthly            — due 15th of next month
 *   2.  ESI monthly               — due 15th of next month
 *   3.  Professional Tax monthly  — due 15th of next month
 *   4.  TDS 24Q quarterly         — due 31st of month after quarter end
 *   5.  GSTR-1 monthly            — due 11th of next month
 *   6.  GSTR-3B monthly           — due 20th of next month
 *   7.  Bonus Form D annual (FY)  — due 30 April (30 days after FY end)
 *   8.  POSH §21 annual           — due 31 January
 *   9.  S&E annual return         — due 31 January
 *  10.  Maternity §11A annual     — due 31 May
 *  11.  LWF biannual              — due 15 July / 15 January
 *
 * Status badge logic (server-side):
 *   "filed"   — corresponding artefact generated in current period
 *   "overdue" — due date in the past, artefact absent
 *   "pending" — due date in the future, artefact absent
 *
 * Design: 4-col card grid (sm: 2-col). Each card shows
 *   title | statute_ref | cadence badge | next_due_date | status badge | action
 * The action links to generator_url (same SPA — React Router Link).
 *
 * No date-picker or period selector — all period maths live server-side
 * anchored to today; HR sees the current obligation set without input.
 *
 * Pattern: mirrors app.$tenantSlug.payroll.statutory.tsx (tile grid) +
 * app.$tenantSlug.accounting.returns.tsx (status/loading pattern).
 */

import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Clock,
  ShieldX,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { isHRAdmin } from "@/lib/nav";
import { complianceApi } from "@/lib/api";
import type { ComplianceItem, ComplianceStatus } from "@/lib/api/compliance";
import { PageHeader } from "@/components/shared/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import { useTenantSlug } from "@/lib/use-tenant-slug";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/compliance",
)({
  component: ComplianceRoute,
});

// ---------------------------------------------------------------------------
// Query key + fetcher hook
// ---------------------------------------------------------------------------

const COMPLIANCE_QUERY_KEY = ["compliance", "dashboard"] as const;

function useComplianceDashboard() {
  return useQuery({
    queryKey: COMPLIANCE_QUERY_KEY,
    queryFn: ({ signal }) => complianceApi.getComplianceDashboard(signal),
    staleTime: 5 * 60 * 1000, // 5 minutes — due dates don't change per second
  });
}

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

const STATUS_LABEL: Record<ComplianceStatus, string> = {
  filed: "Filed",
  pending: "Pending",
  overdue: "Overdue",
};

const STATUS_VARIANT: Record<
  ComplianceStatus,
  "default" | "secondary" | "destructive" | "outline"
> = {
  filed: "default",
  pending: "secondary",
  overdue: "destructive",
};

function StatusBadge({ status }: Readonly<{ status: ComplianceStatus }>) {
  const icon =
    status === "filed" ? (
      <CheckCircle2 className="h-3 w-3" />
    ) : status === "overdue" ? (
      <AlertTriangle className="h-3 w-3" />
    ) : (
      <Clock className="h-3 w-3" />
    );
  return (
    <Badge
      variant={STATUS_VARIANT[status]}
      className="gap-1 text-xs"
      data-testid={`compliance-status-${status}`}
    >
      {icon}
      {STATUS_LABEL[status]}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// ComplianceCard
// ---------------------------------------------------------------------------

interface ComplianceCardProps {
  item: ComplianceItem;
  tenantSlug: string;
}

function ComplianceCard({ item, tenantSlug }: Readonly<ComplianceCardProps>) {
  // Format the date as "15 Jul 2026" for readability.
  const dueDateLabel = (() => {
    try {
      return new Date(item.next_due_date).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      });
    } catch {
      return item.next_due_date;
    }
  })();

  // Resolve the generator_url — may be an absolute path like
  // "/app/my-company/payroll/statutory"; use Link for in-app nav.
  // Strip the /app/{slug}/ prefix so we can build a proper TanStack Link.
  const generatorHref = item.generator_url;

  const cardBorder =
    item.status === "overdue"
      ? "border-destructive/50"
      : item.status === "filed"
        ? "border-green-500/40"
        : "";

  return (
    <Card
      className={cn("flex flex-col", cardBorder)}
      data-testid={`compliance-card-${item.id}`}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <CardTitle className="truncate text-sm font-semibold leading-tight">
              {item.title}
            </CardTitle>
            <CardDescription className="mt-0.5 text-xs">
              {item.statute_ref}
            </CardDescription>
          </div>
          <Badge variant="outline" className="shrink-0 text-xs capitalize">
            {item.cadence}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="mt-auto space-y-3">
        {/* Due date + status */}
        <div className="flex items-center justify-between gap-2 rounded-md bg-muted/40 px-3 py-2">
          <div>
            <p className="text-xs text-muted-foreground">Next due</p>
            <p className="text-sm font-medium">{dueDateLabel}</p>
          </div>
          <StatusBadge status={item.status} />
        </div>

        {/* Generator link — always present */}
        <Button
          asChild
          variant="outline"
          size="sm"
          className="w-full gap-1.5"
          data-testid={`compliance-go-${item.id}`}
        >
          <Link
            to={generatorHref as string}
            params={{ tenantSlug }}
          >
            Go to generator
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Role gate (UX mirror of backend RBAC)
// ---------------------------------------------------------------------------

function ComplianceRoleGate({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  const tenantSlug = useTenantSlug();
  const { user } = useAuth();
  if (isHRAdmin(user?.role)) return <>{children}</>;
  return (
    <>
      <PageHeader
        title="Compliance"
        description="Indian SMB statutory compliance calendar — next due dates at a glance."
      />
      <Card>
        <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent text-accent-foreground">
            <ShieldX className="h-6 w-6" />
          </span>
          <div className="space-y-1">
            <p className="text-base font-medium text-gray-900">
              Compliance is managed by HR admins
            </p>
            <p className="max-w-md text-sm text-muted-foreground">
              Only company admins and HR managers can view the compliance
              dashboard. Ask your HR admin if you need access.
            </p>
          </div>
          <Button asChild variant="outline" className="gap-1.5">
            <Link to="/app/$tenantSlug" params={{ tenantSlug }}>
              Back to dashboard
            </Link>
          </Button>
        </CardContent>
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main route component
// ---------------------------------------------------------------------------

function ComplianceRoute() {
  const { tenantSlug } = useParams({ strict: false }) as {
    tenantSlug: string;
  };

  const { data, isPending, isError, error } = useComplianceDashboard();

  const items = data?.items ?? [];

  return (
    <ComplianceRoleGate>
      <PageHeader
        title="Compliance"
        description="Indian SMB statutory compliance calendar — next due dates at a glance. Green = filed this period, red = overdue, grey = pending."
      />

      {/* Error state */}
      {isError && (
        <Alert variant="destructive" className="mt-4">
          <AlertTitle>Failed to load compliance data</AlertTitle>
          <AlertDescription>
            {(error as Error)?.message ?? "An unexpected error occurred."}
          </AlertDescription>
        </Alert>
      )}

      {/* Summary strip — counts */}
      {!isPending && !isError && items.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-3">
          {(["overdue", "pending", "filed"] as ComplianceStatus[]).map(
            (s) => {
              const count = items.filter((i) => i.status === s).length;
              if (count === 0) return null;
              return (
                <div
                  key={s}
                  className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm"
                >
                  <StatusBadge status={s} />
                  <span className="font-semibold">{count}</span>
                  <span className="text-muted-foreground">
                    obligation{count !== 1 ? "s" : ""}
                  </span>
                </div>
              );
            },
          )}
        </div>
      )}

      {/* Card grid */}
      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {isPending
          ? Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-48" />
            ))
          : items.map((item) => (
              <ComplianceCard
                key={item.id}
                item={item}
                tenantSlug={tenantSlug}
              />
            ))}
      </div>

      {/* Empty state (no items — shouldn't happen but defensive) */}
      {!isPending && !isError && items.length === 0 && (
        <Card className="mt-4">
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <CheckCircle2 className="h-10 w-10 text-muted-foreground" />
            <p className="text-base font-medium">No obligations found</p>
            <p className="text-sm text-muted-foreground">
              The backend returned an empty list. This is unexpected — check
              the backend logs.
            </p>
          </CardContent>
        </Card>
      )}
    </ComplianceRoleGate>
  );
}
